import { forwardRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface AppShellProps {
  /** Top bar slot — compose from the TopBar organism. Sits above the scroll region. */
  topBar?: ReactNode;
  /** Side navigation slot — compose from the SideNav organism (in-flow on lg+, overlay below). */
  sideNav?: ReactNode;
  /** Side panel slot — compose from the SidePanel organism. */
  sidePanel?: ReactNode;
  /** Whether the docked side panel is open — drives the content margin compensation. */
  sidePanelOpen?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * Portal app frame — optional side nav column beside a main column.
 *
 *   <AppShell
 *     topBar={<TopBar … />}
 *     sideNav={<SideNav … />}
 *     sidePanel={<SidePanel … />}
 *     sidePanelOpen={panelOpen}
 *   >
 *     <MyPage />
 *   </AppShell>
 *
 * The main column is: topBar slot on top, then the scrollable content
 * region. That content region (`ds-app-shell-content`) is a container-query
 * container, so ledgers can react to the side panel shrinking it with
 * Tailwind's `@min-*:`/`@max-*:` variants. When `sidePanelOpen` is true the
 * content region gains a margin-right equal to the panel width (transitioned,
 * mirroring the portal's PageContentArea) — below lg the panel is a
 * full-screen overlay, so no compensation applies there.
 */
export const AppShell = forwardRef<HTMLDivElement, AppShellProps>(
  (
    { topBar, sideNav, sidePanel, sidePanelOpen = false, children, className },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn("flex h-dvh w-full overflow-hidden", className)}
      >
        {sideNav}
        <div className="flex min-w-0 flex-1 flex-col">
          {topBar}
          <main
            className={cn(
              "ds-app-shell-content @container relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-canvas transition-[margin-right] duration-200 ease-out",
              sidePanelOpen &&
                "lg:[margin-right:var(--ds-side-panel-width,480px)]",
            )}
            data-panel-open={sidePanelOpen || undefined}
          >
            {children}
          </main>
        </div>
        {sidePanel}
      </div>
    );
  },
);
AppShell.displayName = "AppShell";
